import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { 
  ChevronRight, User, Mail, Phone, MapPin, Package, 
  Clock, CheckCircle, AlertCircle, ArrowLeft, Loader2, 
  Truck, CreditCard, Calendar, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { toAbsoluteUrl } from '../lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, description: 'Votre commande est en attente de traitement' },
  pending_payment: { label: 'En attente paiement', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, description: 'Votre paiement est en attente de confirmation' },
  paid: { label: 'Payée', color: 'bg-green-100 text-green-700 border-green-200', icon: CreditCard, description: 'Paiement reçu' },
  paid_mock: { label: 'Payée (historique test)', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: CreditCard, description: 'Paiement reçu via un ancien flux de test' },
  failed: { label: 'Paiement échoué', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle, description: 'Le paiement a échoué' },
  expired: { label: 'Session expirée', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertCircle, description: 'La session de paiement a expiré' },
  confirmed: { label: 'Confirmée', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, description: 'Votre commande a été confirmée' },
  in_progress: { label: 'En préparation', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Package, description: 'Votre commande est en cours de préparation' },
  shipped: { label: 'Expédiée', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Truck, description: 'Votre commande a été expédiée' },
  completed: { label: 'Livrée', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, description: 'Votre commande a été livrée' },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle, description: 'Votre commande a été annulée' }
};

const getDisplayablePhotoUrl = (photo) => {
  const normalizedUrl = toAbsoluteUrl(photo?.url || photo?.preview || '');
  if (!normalizedUrl || normalizedUrl.startsWith('blob:')) return null;
  return normalizedUrl;
};

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/mon-compte/commandes/${orderId}` } } });
      return;
    }
    fetchOrder();
  }, [orderId, isAuthenticated]);

  const fetchOrder = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/orders/${orderId}`);
      setOrder(response.data);
    } catch (err) {
      console.error('Error fetching order:', err);
      toast.error('Commande non trouvée');
      navigate('/mon-compte');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
        </div>
      </PageTransition>
    );
  }

  if (!order) return null;

  const status = statusConfig[order.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-gradient-to-b from-[var(--champagne)]/30 to-white">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${status.color}`}
          >
            <StatusIcon size={28} />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light mb-2 text-[var(--charcoal)]"
          >
            Commande #{order.id.slice(0, 8).toUpperCase()}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[var(--charcoal)]/60"
          >
            {status.description}
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
            <ChevronRight size={14} />
            <Link to="/mon-compte" className="hover:text-[var(--gold)]">Mon Compte</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Commande #{order.id.slice(0, 8).toUpperCase()}</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-[1000px] mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/mon-compte')}
            className="mb-6"
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour à mon compte
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Status Card */}
              <FadeIn>
                <div className={`p-6 border ${status.color}`}>
                  <div className="flex items-center gap-3">
                    <StatusIcon size={24} />
                    <div>
                      <h3 className="font-medium">{status.label}</h3>
                      <p className="text-sm opacity-80">{status.description}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>

              {/* Order Items */}
              <FadeIn delay={0.1}>
                <div className="bg-white p-6 border border-[var(--champagne)]">
                  <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)] flex items-center gap-2">
                    <Package size={20} className="text-[var(--gold)]" />
                    Contenu de la commande
                  </h3>
                  
                  {/* Physical Items */}
                  {order.items && order.items.length > 0 && (
                    <div className="space-y-4 mb-6">
                      <h4 className="text-sm font-medium text-[var(--charcoal)]/70 uppercase tracking-wider">
                        Articles physiques
                      </h4>
                      {order.items.map((item, idx) => {
                        const firstPhotoUrl = getDisplayablePhotoUrl(item.photos?.[0]);
                        const isFormationProduct = item.type === 'formation_product';
                        return (
                          <div key={idx} className="flex gap-4 p-4 bg-[var(--champagne)]/20">
                            <div className="w-20 h-20 shrink-0 bg-white border border-[var(--champagne)] flex items-center justify-center">
                              {firstPhotoUrl ? (
                                <img src={firstPhotoUrl} alt="Preview" className="w-full h-full object-cover" />
                              ) : (
                                <Package size={28} className="text-[var(--gold)]" />
                              )}
                            </div>

                            <div className="flex-1">
                              <h4 className="font-medium text-[var(--charcoal)]">
                                {isFormationProduct ? item.product_name || 'Produit formation' : 'Press On Nails Personnalisés'}
                              </h4>
                              {isFormationProduct ? (
                                <p className="text-sm text-[var(--charcoal)]/70 mt-2">Produit physique</p>
                              ) : (
                                <>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-[var(--charcoal)]/70 mt-2">
                                    <div><span className="text-[var(--charcoal)]/50">Forme:</span> {item.shapeLabel || item.shape}</div>
                                    <div><span className="text-[var(--charcoal)]/50">Longueur:</span> {item.lengthLabel || item.length}</div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-[var(--charcoal)]/50">Couleur:</span>
                                      <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: item.color }} />
                                      {item.colorLabel && <span>{item.colorLabel}</span>}
                                    </div>
                                    {item.nailArt && item.nailArt !== 'none' && (
                                      <div><span className="text-[var(--charcoal)]/50">Nail Art:</span> {item.nailArtLabel || item.nailArt}</div>
                                    )}
                                  </div>
                                  {item.nailArtDescription && (
                                    <p className="mt-2 text-xs text-[var(--charcoal)]/50 italic">"{item.nailArtDescription}"</p>
                                  )}
                                  {item.photos && item.photos.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-[var(--champagne)]/50">
                                      <p className="text-xs text-[var(--charcoal)]/50 mb-2">Photos de référence :</p>
                                      <div className="flex gap-2 flex-wrap">
                                        {item.photos.map((photo, photoIdx) => {
                                          const photoUrl = getDisplayablePhotoUrl(photo);
                                          if (!photoUrl) {
                                            return (
                                              <div key={photoIdx} className="w-16 h-16 border border-[var(--champagne)] bg-white text-[10px] text-[var(--charcoal)]/50 flex items-center justify-center text-center px-1">
                                                Indispo
                                              </div>
                                            );
                                          }
                                          return (
                                            <a key={photoIdx} href={photoUrl} target="_blank" rel="noopener noreferrer" className="block relative group">
                                              <img src={photoUrl} alt={photo.type === 'left_hand' ? 'Main gauche' : photo.type === 'right_hand' ? 'Main droite' : photo.type === 'inspiration' ? 'Inspiration' : `Photo ${photoIdx + 1}`} className="w-16 h-16 object-cover border border-[var(--champagne)] hover:opacity-80 transition-opacity" />
                                              <span className="absolute -bottom-4 left-0 right-0 text-center text-[10px] text-[var(--charcoal)]/60">
                                                {photo.type === 'left_hand' && 'Gauche'}
                                                {photo.type === 'right_hand' && 'Droite'}
                                                {photo.type === 'inspiration' && 'Inspi.'}
                                                {!photo.type && `#${photoIdx + 1}`}
                                              </span>
                                            </a>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>

                            <div className="text-right">
                              <p className="font-['Cormorant_Garamond'] text-xl text-[var(--gold)]">
                                {item.price?.toFixed(2)}€
                              </p>
                            </div>
                          </div>
                        );
                      })}
                                </div>
                  )}

                  {/* Gift Card Items */}
                  {order.gift_card_details && order.gift_card_details.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-[var(--charcoal)]/70 uppercase tracking-wider">
                        Cartes Cadeaux
                      </h4>
                      {order.gift_card_details.map((gc, idx) => (
                        <div key={idx} className="flex gap-4 p-4 bg-gradient-to-r from-[var(--rose-gold)]/10 to-[var(--champagne)]/20 border border-[var(--rose-gold)]/20">
                          {/* Gift card icon */}
                          <div className="w-20 h-20 shrink-0 bg-white border border-[var(--rose-gold)]/30 flex items-center justify-center">
                            <Gift size={32} className="text-[var(--gold)]" />
                          </div>
                          
                          {/* Gift card details */}
                          <div className="flex-1">
                            <h4 className="font-medium text-[var(--charcoal)]">
                              Carte Cadeau
                              {gc.prestation_name && (
                                <span className="font-normal text-[var(--charcoal)]/60"> — {gc.prestation_name}</span>
                              )}
                            </h4>
                            <div className="space-y-1 text-sm text-[var(--charcoal)]/70 mt-2">
                              <div>
                                <span className="text-[var(--charcoal)]/50">Type:</span>{' '}
                                {gc.gift_type === 'preset_amount' && 'Montant prédéfini'}
                                {gc.gift_type === 'custom_amount' && 'Montant personnalisé'}
                                {gc.gift_type === 'prestation' && 'Prestation'}
                              </div>
                              {gc.receiver_name && (
                                <div>
                                  <span className="text-[var(--charcoal)]/50">Pour:</span> {gc.receiver_name}
                                </div>
                              )}
                              {gc.sender_name && (
                                <div>
                                  <span className="text-[var(--charcoal)]/50">De:</span> {gc.sender_name}
                                </div>
                              )}
                              {gc.code && (
                                <div className="mt-2">
                                  <span className="text-[var(--charcoal)]/50">Code:</span>{' '}
                                  <span className="font-mono bg-[var(--champagne)] px-2 py-1 text-[var(--gold)]">
                                    {gc.code}
                                  </span>
                                </div>
                              )}
                            </div>
                            {gc.personal_message && (
                              <p className="mt-2 text-xs text-[var(--charcoal)]/50 italic">
                                "{gc.personal_message}"
                              </p>
                            )}
                          </div>
                          
                          {/* Price */}
                          <div className="text-right">
                            <p className="font-['Cormorant_Garamond'] text-xl text-[var(--gold)]">
                              {gc.amount?.toFixed(2)}€
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No items */}
                  {(!order.items || order.items.length === 0) && (!order.gift_card_details || order.gift_card_details.length === 0) && (
                    <p className="text-[var(--charcoal)]/60">Aucun article</p>
                  )}
                </div>
              </FadeIn>

              {/* Customer Info */}
              {order.customer_info && (
                <FadeIn delay={0.2}>
                  <div className="bg-white p-6 border border-[var(--champagne)]">
                    <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)] flex items-center gap-2">
                      <User size={20} className="text-[var(--gold)]" />
                      Informations client
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <User size={16} className="text-[var(--charcoal)]/40" />
                        <span>
                          {order.customer_info.first_name} {order.customer_info.last_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail size={16} className="text-[var(--charcoal)]/40" />
                        <span>{order.customer_info.email}</span>
                      </div>
                      {order.customer_info.phone && (
                        <div className="flex items-center gap-3">
                          <Phone size={16} className="text-[var(--charcoal)]/40" />
                          <span>{order.customer_info.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </FadeIn>
              )}

              {/* Shipping Address */}
              {(order.shipping_address_details || order.shipping_address) && (
                <FadeIn delay={0.3}>
                  <div className="bg-white p-6 border border-[var(--champagne)]">
                    <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)] flex items-center gap-2">
                      <MapPin size={20} className="text-[var(--gold)]" />
                      Adresse de livraison
                    </h3>
                    {order.shipping_address_details ? (
                      <div className="text-[var(--charcoal)]/70">
                        <p>{order.shipping_address_details.street}</p>
                        {order.shipping_address_details.complement && (
                          <p>{order.shipping_address_details.complement}</p>
                        )}
                        <p>
                          {order.shipping_address_details.postal_code} {order.shipping_address_details.city}
                        </p>
                        <p>{order.shipping_address_details.country}</p>
                      </div>
                    ) : (
                      <p className="text-[var(--charcoal)]/70">{order.shipping_address}</p>
                    )}
                  </div>
                </FadeIn>
              )}

              {/* Notes */}
              {order.notes && (
                <FadeIn delay={0.4}>
                  <div className="bg-white p-6 border border-[var(--champagne)]">
                    <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                      Notes
                    </h3>
                    <p className="text-[var(--charcoal)]/70">{order.notes}</p>
                  </div>
                </FadeIn>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Price Summary */}
              <FadeIn>
                <div className="bg-white p-6 border border-[var(--champagne)]">
                  <h3 className="font-['Cormorant_Garamond'] text-xl mb-4 text-[var(--charcoal)]">
                    Récapitulatif
                  </h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--charcoal)]/60">Sous-total</span>
                      <span>{order.subtotal?.toFixed(2) || '—'}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--charcoal)]/60 flex items-center gap-1">
                        <Truck size={14} />
                        Livraison
                      </span>
                      <span className={order.delivery_fee === 0 ? 'text-green-600 font-medium' : ''}>
                        {order.delivery_fee === 0 ? 'Offerte' : `${order.delivery_fee?.toFixed(2)}€`}
                      </span>
                    </div>
                    <div className="border-t border-[var(--champagne)] pt-3 flex justify-between items-center">
                      <span className="font-medium text-[var(--charcoal)]">Total</span>
                      <span className="font-['Cormorant_Garamond'] text-2xl text-[var(--gold)]">
                        {order.total_price?.toFixed(2)}€
                      </span>
                    </div>
                  </div>
                </div>
              </FadeIn>

              {/* Order Info */}
              <FadeIn delay={0.1}>
                <div className="bg-[var(--champagne)]/30 p-6 border border-[var(--champagne)]">
                  <h4 className="text-sm uppercase tracking-wider text-[var(--charcoal)]/60 mb-4">
                    Informations
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2 text-[var(--charcoal)]/70">
                      <Calendar size={14} className="text-[var(--gold)]" />
                      <span>Commandé le {formatDate(order.created_at)}</span>
                    </div>
                    {order.payment_method && (
                      <div className="flex items-center gap-2 text-[var(--charcoal)]/70">
                        <CreditCard size={14} className="text-[var(--gold)]" />
                        <span>Paiement : {order.payment_method === 'mock' ? 'Mode test' : order.payment_method}</span>
                      </div>
                    )}
                  </div>
                </div>
              </FadeIn>

              {/* Help */}
              <FadeIn delay={0.2}>
                <div className="bg-white p-6 border border-[var(--champagne)]">
                  <h4 className="font-medium text-[var(--charcoal)] mb-2">Besoin d'aide ?</h4>
                  <p className="text-sm text-[var(--charcoal)]/60 mb-4">
                    Contactez-nous pour toute question concernant votre commande.
                  </p>
                  <Link
                    to="/#contact"
                    className="text-sm text-[var(--dark-rosegold)] hover:text-[var(--gold)] transition-colors"
                  >
                    Nous contacter →
                  </Link>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
